import {
    IonBadge,
    IonIcon,
    IonItem,
    IonItemOption,
    IonItemOptions,
    IonItemSliding,
    IonLabel,
} from '@ionic/react';
import { Plural, Trans } from '@lingui/macro';
import { call } from 'ionicons/icons';
import { useCallback } from 'react';

import { IJobList } from '../../interfaces/IJob';
import { TParseJob, TParseJobResponse } from '../../types/Job';
import { ODOO_INST_TYPE, ROOT_URL } from '../../utils/constants';
import { timestampToTime } from '../../utils/date';
import { useCustomRouter } from '../../utils/hooks/useCustomRouter';
import classes from './JobItem.module.css';

interface IJobItemProps {
    job: IJobList;
    viewingPastJobs: boolean;
}

const parseJob: TParseJob = (job: IJobList) => {
    const planned = timestampToTime(job.dateStartLocal || job.dateStart);
    const contact = `${job.customerId[1] || ''}`;

    const addressNumber = job.number || '';
    const addressLine1 = `${job.street || ''} ${job.number || ''} ${
        job.street2 || ''
    }`;
    const addressLine2 = `${job.city || ''} ${job.zip || ''}`;
    const address = `${addressNumber} ${addressLine1} ${addressLine2}`;
    const nbHeatings = job.nbHeatings || 0;

    const response: TParseJobResponse = {
        planned,
        contact,
        address,
        nbHeatings,
    };

    return response;
};

const JobItem = (props: IJobItemProps) => {
    const { job, viewingPastJobs } = props;
    const { planned, contact, address, nbHeatings } = parseJob(job);
    const router = useCustomRouter();
    const isPRO =
        job.organisationalUnits?.filter((organisationalUnit) =>
            organisationalUnit.includes(`_PRO`)
        ).length > 0;
    const isV2 = !job.isV3;
    let backgroundColorClass = '';
    if (job.typeInstallation === ODOO_INST_TYPE.UNINSTALL) {
        backgroundColorClass = classes.uninstallBackground;
    } else if (job.typeInstallation === ODOO_INST_TYPE.REPAIR) {
        backgroundColorClass = classes.repairBackground;
    }
    // if (isPRO) {
    //     backgroundColorClass = classes.proBackground;
    // }

    const onCallCustomer = useCallback(() => {
        window.open(`tel:${job.mobile}`, '_system');
    }, [job.mobile]);

    const onItemClickHandler = useCallback(() => {
        if (isV2) {
            let redirectUrl = `${ROOT_URL}/installation/customer_list/`;
            if (viewingPastJobs) {
                redirectUrl += '?past';
            }
            window.open(redirectUrl, '_blank', 'noopener, noreferrer');
        } else {
            viewingPastJobs
                ? router.push(`/${job.id}/past-job`)
                : router.push(`/${job.id}`);
        }
    }, [isV2, job.id, router, viewingPastJobs]);

    const item = (
        <IonItem
            detail={!isV2}
            onClick={onItemClickHandler}
            className={backgroundColorClass}
        >
            <IonLabel>
                <h2>{contact}</h2>
                <p>
                    {nbHeatings ? (
                        <Plural
                            value={nbHeatings}
                            one={`# heater at ${planned} in ${address}`}
                            other={`# heaters at ${planned} in ${address}`}
                        />
                    ) : (
                        <Trans>
                            At {planned} in {address}
                        </Trans>
                    )}
                </p>
            </IonLabel>
            {isV2 && <IonBadge slot="end">v2</IonBadge>}
            {isPRO && (
                <IonBadge className="ion-margin-start" slot="end">
                    Pro
                </IonBadge>
            )}
        </IonItem>
    );

    return job.mobile ? (
        <IonItemSliding>
            {item}
            <IonItemOptions>
                <IonItemOption>
                    <IonIcon
                        className="ion-padding"
                        icon={call}
                        onClick={onCallCustomer}
                    ></IonIcon>
                </IonItemOption>
            </IonItemOptions>
        </IonItemSliding>
    ) : (
        item
    );
};

export default JobItem;
